import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { Observable, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';

import { Constants, SnackBarHelperComponent, TEXT, ValidatorHelper } from '../../../helpers';
import { Competitor, GetAddressValidation, GetProjectStatusValidation, Project } from '../../../interfaces';
import { ProjectRouteService } from '../../../pages/project/common/project.service';
import { CompetitorService } from '../../../services/competitor.service';
import { ProjectService } from '../../../services/project.service';
import { RolePermissionsService } from '../../../services/role-permissions.service';
import { UserModel } from '../../../models';
import { UserService } from '../../../services/user.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { projectStatuses } from '../../../components/project/common/project.constants';

@Component({
	selector: 'app-edit-project-status-dialog',
	templateUrl: './edit-project-status-dialog.component.html',
	styleUrls: ['./edit-project-status-dialog.component.scss'],
})
export class EditProjectStatusDialogComponent implements OnInit {
	@Input() public newProjectStatus?: string;
	@Input() public set project(project: Project) {
		this._project = project;
		// project.addressDetail won't be included when the project is passed from the board view, so we need to fetch the whole project
		if (project?._id && !project.addressDetail) {
			this.fetchFullProject(this._project._id);
		} else {
			this.initializeProjectData(project);
		}
	}

	public get project(): Project {
		return this._project;
	}

	@Output() public projectUpdated: EventEmitter<Project> = new EventEmitter<Project>();

	public competitors: Competitor[] = [];
	public currentDate: Date = new Date();
	public finishDate: Date;
	public LABELS = Constants.EDIT_PROJECT_STATUS_LABELS;
	public lostCompetitor: Competitor;
	public PROJECT_STATUS = Constants.PROJECT_STATUS;
	public PROJECT_STATUS_ARRAY = Constants.PROJECT_STATUS_ARRAY;
	public PROJECT_STATUS_REASONS = Constants.PROJECT_STATUS_REASONS;
	public projectStatus: string;
	public quoteDueDate: Date;
	public savingStatusPromise: Promise<Project>;
	public selectedClient: string;
	public selectedContractAdministrator: string;
	public selectedProjectManager: string;
	public siteAddressValidators: { [key: string]: UntypedFormControl };
	public startDate: Date;
	public statusDate: Date;
	public statusOtherReason: string;
	public statusReason: string;
	public submittedDate: Date;
	public statusValidators;
	public textConstants: typeof TEXT = TEXT;
	public users: UserModel[] = [];

	private _project: Project;
	private destroyRef: DestroyRef = inject(DestroyRef);

	constructor(
		private projectRouteService: ProjectRouteService,
		private projectService: ProjectService,
		private competitorService: CompetitorService,
		private rolePermissionsService: RolePermissionsService,
		private userService: UserService,
		private snack: SnackBarHelperComponent,
	) {}

	public ngOnInit(): void {
		this.populateDropdowns();
		this.filterStatusOptionsForUser();
		this.statusValidators = GetProjectStatusValidation();
		this.siteAddressValidators = GetAddressValidation(this._project.addressDetail, this._project.status, true);
		this.updateStatusValidation();
	}

	private populateDropdowns(): void {
		this.competitorService
			.postList({ params: { isActive: true } })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(competitors => {
				this.competitors = competitors;
			});

		this.userService
			.getUsers({ isActive: true })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(users => {
				this.users = users;
			});
	}

	private filterStatusOptionsForUser(): void {
		// Only team leaders and above can approve projects (won/lost/quoteApproved)
		if (!this.rolePermissionsService.isTeamLeaderAndAbove()) {
			this.PROJECT_STATUS_ARRAY = this.PROJECT_STATUS_ARRAY.filter(status => {
				return (
					status !== this.PROJECT_STATUS.won &&
					status !== this.PROJECT_STATUS.lost &&
					status !== this.PROJECT_STATUS.quoteApproved &&
					status !== this.PROJECT_STATUS.submitted
				);
			});
		}
	}

	private fetchFullProject(projectId: string): void {
		this.projectService
			.getProject(projectId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((fullProject: Project) => {
				this._project = fullProject;
				this.initializeProjectData(fullProject);
			});
	}

	private initializeProjectData(project: Project): void {
		// ensure we actually have the project before attempting to set the fields and update the validation
		if (project?.id) {
			this.projectStatus = this.newProjectStatus ?? project.status;
			this.quoteDueDate = project.quoteDueDate;
			this.statusReason = project.statusReason;
			this.statusOtherReason = project.statusOtherReason;
			this.submittedDate = project.submittedDate;
			this.statusDate = project.statusDate ?? this.currentDate;
			this.startDate = project.projectStartDate;
			this.finishDate = project.projectFinishDate;
			this.lostCompetitor = project.lostCompetitor;
			this.selectedClient = project.clientWonLost
				? project.clientWonLost
				: project?.clients?.length === 1
					? project?.clients[0]?.company.id
					: null;
			this.selectedContractAdministrator = project.contractAdministrator?.id;
			this.selectedProjectManager = project.projectManager?.id;
			// Update the status validation once the fields have been set
			this.updateStatusValidation();
		}
	}

	/**
	 * Indicate if the current project status needs a client
	 */
	public statusRequiresClient(): boolean {
		return [
			this.PROJECT_STATUS.lost.value,
			this.PROJECT_STATUS.won.value,
			this.PROJECT_STATUS.live.value,
			this.PROJECT_STATUS.completed.value,
		].includes(this.projectStatus);
	}

	/**
	 * Indicate if the current project status is of a "won" type
	 */
	public isWonTypeStatus(): boolean {
		return [this.PROJECT_STATUS.won.value, this.PROJECT_STATUS.live.value, this.PROJECT_STATUS.completed.value].includes(this.projectStatus);
	}

	/**
	 * Indicate if the current project status is of a "submitted" or "won" type
	 */
	public isSubmittedWonTypeStatus(): boolean {
		return [
			this.PROJECT_STATUS.submitted.value,
			this.PROJECT_STATUS.won.value,
			this.PROJECT_STATUS.live.value,
			this.PROJECT_STATUS.completed.value,
		].includes(this.projectStatus);
	}

	/**
	 * Indicate if the current project status should clear the status reason
	 */
	public shouldClearStatusReason(): boolean {
		return [
			this.PROJECT_STATUS.target.value,
			this.PROJECT_STATUS.active.value,
			this.PROJECT_STATUS.quoteReady.value,
			this.PROJECT_STATUS.quoteApproved.value,
			this.PROJECT_STATUS.submitted.value,
		].includes(this.projectStatus);
	}

	/**
	 * Indicate if the status reason of the project is 'Other'
	 */
	public hasOtherStatusReason(): boolean {
		return this.statusReason === 'Other';
	}

	/**
	 * Tell if the current project status needs a reason
	 */
	public hasStatusReason(): boolean {
		return !!this.PROJECT_STATUS_REASONS[this.projectStatus];
	}

	/**
	 * Cleans WonLostClient inputs on Competitor changes
	 */
	public onChangeCompetitor(): void {
		this.selectedClient = null;
	}

	/**
	 * Set/Update project status field's validators
	 * @private
	 */
	private updateStatusValidation() {
		if (this.projectStatus !== this.PROJECT_STATUS.active.value) {
			this.statusValidators.QuoteDueDate.errors = null;
		}
		if (!this.isSubmittedWonTypeStatus()) {
			this.statusValidators.SubmittedDate.errors = null;
		} else if (!this.submittedDate) {
			this.statusValidators.SubmittedDate.errors = { required: true };
		}
		// If not won, lost or decline status then valid the status reason validator
		if (!this.hasStatusReason()) {
			this.statusValidators.StatusReason.errors = null;
		}
		if (!this.hasOtherStatusReason()) {
			this.statusValidators.StatusOtherReason.errors = null;
		}
		if (!this.statusRequiresClient()) {
			this.statusValidators.Client.errors = null;
			this.selectedClient = undefined;
		}
		if (!this.isWonTypeStatus()) {
			this.statusValidators.StartDate.errors = null;
			this.statusValidators.FinishDate.errors = null;
			this.statusValidators.ContractAdministrator.errors = null;
			this.statusValidators.ProjectManager.errors = null;
		}
		if (this.isWonTypeStatus()) {
			this.siteAddressValidators = GetAddressValidation(this._project.addressDetail, this.projectStatus, true);
			if (!this.startDate) this.statusValidators.StartDate.errors = { required: true };
			if (!this.finishDate) this.statusValidators.FinishDate.errors = { required: true };
		}
		if (this.projectStatus) {
			this.statusValidators.StatusDate.errors = null;
		}
	}

	/**
	 * Clean status reason and other reason inputs on project status changes
	 */
	public onChangeProjectStatus(): void {
		// whenever we change the status, we want to update the statusDate to today
		// if the user doesn't save the change, it will revert to the previous date
		this.statusDate = this.currentDate;
		if (this.shouldClearStatusReason()) {
			this.statusReason = undefined;
			this.onChangeStatusReason();
		}
		this.updateStatusValidation();
	}

	public onChangeDate(date: Date, dateType: 'statusDate' | 'submittedDate' | 'startDate' | 'finishDate' | 'quoteDueDate'): void {
		if (date && this.hasDateChanged(this[dateType], date)) {
			const currentTime = this.currentDate;
			date.setHours(currentTime.getHours());
			date.setMinutes(currentTime.getMinutes());
			date.setSeconds(currentTime.getSeconds());

			switch (dateType) {
				case 'statusDate': {
					this.statusDate = date;
					this.statusValidators.StatusDate = date;
					break;
				}
				case 'submittedDate': {
					this.submittedDate = date;
					this.statusValidators.SubmittedDate = date;
					break;
				}
				case 'startDate': {
					this.startDate = date;
					this.statusValidators.StartDate = date;
					break;
				}
				case 'finishDate': {
					this.finishDate = date;
					this.statusValidators.FinishDate = date;
					break;
				}
				case 'quoteDueDate': {
					this.quoteDueDate = date;
					this.statusValidators.QuoteDueDate = date;
					break;
				}
			}
		}
	}

	// Helper function to check if only the date part has changed (ignores time)
	private hasDateChanged(oldDate: Date, newDate: Date): boolean {
		if (!oldDate || !newDate || !(oldDate instanceof Date) || !(newDate instanceof Date)) {
			return true;
		}

		return (oldDate.getFullYear() !== newDate.getFullYear() || oldDate.getMonth() !== newDate.getMonth() || oldDate.getDate() !== newDate.getDate());
	}

	/**
	 * Clean other reason input on project status reason changes
	 */
	public onChangeStatusReason(): void {
		this.statusOtherReason = undefined;
		this.lostCompetitor = undefined;
		// If no 'Other' status reason, then set the status other reason validator to valid
		if (!this.hasOtherStatusReason()) {
			this.statusValidators.StatusOtherReason.errors = null;
		}
	}

	/**
	 * Save project status
	 */
	public onSaveProjectStatus(): Observable<Project> {
		if (this.projectStatus && this.statusDate) {
			this.statusValidators.StatusDate.errors = null;
		}
		const statusErrors = ValidatorHelper.checkErrors(this.statusValidators);
		const addressErrors = ValidatorHelper.checkErrors(this.siteAddressValidators);

		let errors = '';
		if (statusErrors) {
			errors = statusErrors;
		}

		if (this.isWonTypeStatus() && addressErrors) {
			errors = errors + addressErrors;
		}

		if (errors.length) {
			this.snack.snackError(errors);
			return throwError(errors);
		}

		return this.projectService
			.editProjectStatus(
				this.project.id,
				this.projectStatus,
				this.statusDate,
				this.statusReason,
				this.statusOtherReason,
				this.lostCompetitor,
				this.selectedClient,
				this.quoteDueDate,
				this.submittedDate,
				this.startDate,
				this.finishDate,
				this.selectedContractAdministrator,
				this.selectedProjectManager,
				this.project.addressDetail,
			)
			.pipe(
				tap(project => {
					this.projectRouteService.setProject(project);
					this.projectUpdated.emit(project);
					return project;
				}),
			);
	}

	protected readonly projectStatuses = projectStatuses;
}
